import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "./register-pd-logo-loader.js";
import { LitElement, css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import "@progressive-development/pd-icon/pd-icon";
import { localized, msg } from "@lit/localize";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-loading-state/PdLoadingState.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var PdLoadingState = class PdLoadingState extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.loaderStyle = "spinner";
		this.inline = false;
	}
	static {
		this.styles = [css`
      :host {
        display: block;
      }

      /* Modal overlay (fullscreen) */
      .modal {
        position: fixed;
        left: 0;
        top: 0;
        z-index: 500;

        display: flex;
        align-items: center;
        justify-content: center;

        width: 100%;
        height: 100%;
        overflow: auto;
        background-color: var(
          --pd-popup-modal-bg-rgba,
          rgba(175, 193, 210, 0.8)
        );
      }

      /* Centered content without modal */
      .no-modal-content {
        position: fixed;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      /* Main content box */
      .content {
        padding: var(--pd-spacing-lg, 2rem) var(--pd-spacing-lg, 2rem);
        text-align: center;
        background-color: var(
          --pd-loading-state-bg-col,
          var(--pd-default-bg-light-col, #f2f9fa)
        );
        border: var(--pd-border-width, 1px) solid
          var(--pd-loading-state-border-col, var(--pd-default-col, #067394));
        border-radius: var(--pd-radius-lg, 8px);
        box-shadow: var(--pd-shadow-lg, 0 4px 8px rgba(0, 0, 0, 0.15));
        min-width: 280px;
        max-width: 420px;
        z-index: 101;
      }

      /* Background mode (bottom-right pill/toast) */
      .background-content {
        position: fixed;
        right: var(--pd-spacing-md, 1rem);
        bottom: var(--pd-spacing-md, 1rem);
        padding: var(--pd-spacing-sm, 0.5rem) var(--pd-spacing-md, 1rem);
        z-index: 600;
        display: flex;
        gap: var(--pd-spacing-sm, 0.5rem);
        align-items: center;
        background: var(
          --pd-loading-state-background-bg,
          var(--pd-default-bg-col, #fff)
        );
        border-radius: var(--pd-radius-lg, 8px);
        box-shadow: var(--pd-shadow-lg, 0 4px 8px rgba(0, 0, 0, 0.15));
        font-size: 0.8125rem;
        color: var(--pd-default-font-content-col, #353738);
      }

      /* ====== Spinner loader ====== */
      .loader-spinner {
        border: 4px solid
          var(--pd-loading-state-loader-bg, var(--pd-default-dark-col, #0a3a48));
        border-top: 4px solid
          var(--pd-loading-state-loader-fg, var(--pd-default-col, #067394));
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: spin 2s linear infinite;
        margin-left: auto;
        margin-right: auto;
      }

      /* ====== Dots loader ====== */
      .loader-dots {
        display: flex;
        gap: 8px;
        justify-content: center;
      }
      .loader-dots span {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(
          --pd-loading-state-loader-fg,
          var(--pd-default-col, #067394)
        );
        animation: dot-pulse 1.4s ease-in-out infinite;
      }
      .loader-dots span:nth-child(2) {
        animation-delay: 0.2s;
      }
      .loader-dots span:nth-child(3) {
        animation-delay: 0.4s;
      }

      /* ====== Pulse loader ====== */
      .loader-pulse {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        margin: 0 auto;
        border: 3px solid
          var(--pd-loading-state-loader-fg, var(--pd-default-col, #067394));
        animation: pulse-ring 1.5s ease-in-out infinite;
      }

      /* ====== Background spinner (small) ====== */
      .background-loader {
        border: 2px solid
          var(--pd-loading-state-loader-bg, var(--pd-default-dark-col, #0a3a48));
        border-top: 2px solid
          var(--pd-loading-state-loader-fg, var(--pd-default-col, #067394));
        border-radius: 50%;
        width: 16px;
        height: 16px;
        animation: spin 2s linear infinite;
        flex-shrink: 0;
      }

      /* ====== Sub-tasks ====== */
      .sub-tasks {
        margin-top: var(--pd-spacing-md, 1rem);
        text-align: left;
      }
      .sub-task-header {
        font-size: 0.75rem;
        color: var(--pd-default-font-muted-col, #6b7280);
        margin-bottom: var(--pd-spacing-sm, 0.5rem);
      }
      .sub-task-row {
        display: flex;
        align-items: center;
        gap: var(--pd-spacing-sm, 0.5rem);
        padding: var(--pd-spacing-xs, 0.25rem) 0;
      }
      .sub-task-row .sub-icon {
        --pd-icon-size: 1rem;
        padding: 0;
        margin: 0;
        flex-shrink: 0;
      }
      .sub-task-row .completed {
        --pd-icon-col-active: var(
          --pd-loading-state-completed-col,
          var(--pd-default-success-col, #348b11)
        );
      }
      .sub-task-row .loading {
        --pd-icon-col-active: var(
          --pd-loading-state-loading-col,
          var(--pd-default-warning-col, #ffbf00)
        );
        animation: spin 2s linear infinite;
      }

      /* ====== Keyframes ====== */
      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      @keyframes dot-pulse {
        0%,
        80%,
        100% {
          opacity: 0.3;
          transform: scale(0.8);
        }
        40% {
          opacity: 1;
          transform: scale(1);
        }
      }

      @keyframes pulse-ring {
        0% {
          transform: scale(0.8);
          opacity: 1;
        }
        100% {
          transform: scale(1.4);
          opacity: 0;
        }
      }
    `];
	}
	render() {
		if (!this.loadingState) return nothing;
		return this.loadingState.modal ? html`
          <div
            id="modalId"
            class="modal"
            role="dialog"
            aria-modal="true"
            aria-busy="true"
            aria-label="${msg("Ladevorgang", { id: "pdContent.loadingstate.ariaLabel" })}"
          >
            ${this._renderContent()}
          </div>
        ` : this._renderContent();
	}
	_renderLoader() {
		switch (this.loaderStyle) {
			case "dots": return html`<div
          class="loader-dots"
          role="progressbar"
          aria-label="${msg("Laden", { id: "pdContent.loadingstate.ariaLoading" })}"
        >
          <span></span><span></span><span></span>
        </div>`;
			case "pulse": return html`<div
          class="loader-pulse"
          role="progressbar"
          aria-label="${msg("Laden", { id: "pdContent.loadingstate.ariaLoading" })}"
        ></div>`;
			case "logo": return html`<pd-logo-loader
          role="progressbar"
          aria-label="${msg("Laden", { id: "pdContent.loadingstate.ariaLoading" })}"
        ></pd-logo-loader>`;
			default: return html`<div
          class="loader-spinner"
          role="progressbar"
          aria-label="${msg("Laden", { id: "pdContent.loadingstate.ariaLoading" })}"
        ></div>`;
		}
	}
	_renderSubTasks(subTasks) {
		const completed = subTasks.filter((t) => t.completed).length;
		return html`
      <div class="sub-tasks">
        <div class="sub-task-header">
          ${completed} von ${subTasks.length} Schritten
        </div>
        ${subTasks.map((task) => html`
            <div class="sub-task-row">
              ${task.completed ? html`<pd-icon
                      class="sub-icon completed"
                      icon="checkBox"
                      activeIcon
                    ></pd-icon>` : html`<pd-icon
                      class="sub-icon loading"
                      icon="syncIcon"
                      activeIcon
                    ></pd-icon>`}
              <span>${task.loadingTxt}</span>
            </div>
          `)}
      </div>
    `;
	}
	_renderContent() {
		if (!this.loadingState) return nothing;
		if (this.loadingState.smallBackground) return html`
        <div class="background-content" role="status" aria-live="polite">
          <div
            class="background-loader"
            role="progressbar"
            aria-label="${msg("Laden", { id: "pdContent.loadingstate.ariaLoading" })}"
          ></div>
          <span>
            ${this.loadingState.loadingTxt || msg("Daten werden synchronisiert", { id: "pdContent.loadingstate.syncState" })}
          </span>
        </div>
      `;
		return html`
      <div
        class="${classMap({
			content: true,
			"no-modal-content": this.loadingState.modal !== true && !this.inline
		})}"
        role="status"
        aria-live="polite"
      >
        ${this._renderLoader()}
        <p>
          ${this.loadingState.loadingTxt || msg("Bitte warten, Daten werden geladen", { id: "pdContent.loadingstate.pleaseWait" })}
        </p>
        ${this.loadingState.subTask && this.loadingState.subTask.length > 0 ? this._renderSubTasks(this.loadingState.subTask) : nothing}
      </div>
    `;
	}
};
__decorate([property({ type: Object })], PdLoadingState.prototype, "loadingState", void 0);
__decorate([property({
	type: String,
	reflect: true
})], PdLoadingState.prototype, "loaderStyle", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdLoadingState.prototype, "inline", void 0);
PdLoadingState = __decorate([localized()], PdLoadingState);
//#endregion
export { PdLoadingState };
